
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Database, Trash2, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';

export const DummyDataManager = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const generateDummyData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Carbon calculations dummy data
      const carbonCalculations = Array.from({ length: 8 }, (_, i) => ({
        user_id: user.id,
        calculation_type: ['energy', 'transport', 'waste', 'production', 'chemical', 'water'][i % 6],
        activity_name: ['Office Electricity', 'Fleet Vehicles', 'Waste Disposal', 'Manufacturing', 'Air Travel', 'Chemical Processing', 'Water Treatment', 'Refrigeration'][i],
        amount: Math.random() * 1000 + 100,
        unit: ['kWh', 'liters', 'kg', 'units', 'km', 'tons', 'm3', 'hours'][i % 8],
        emission_factor: Math.random() * 5 + 0.1,
        co2_equivalent: Math.random() * 500 + 50,
        scope: ['scope1', 'scope2', 'scope3'][i % 3],
        calculation_date: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        local_currency: ['USD', 'EUR', 'NGN', 'GBP', 'JPY'][i % 5],
        local_amount: Math.random() * 10000 + 1000,
        exchange_rate: [1, 0.92, 1650, 0.79, 149.5][i % 5],
        usd_amount: Math.random() * 8000 + 500
      }));

      // ESG data points
      const esgDataPoints = Array.from({ length: 12 }, (_, i) => ({
        user_id: user.id,
        metric_name: [
          'Energy Consumption', 'Water Usage', 'Waste Generated', 'Employee Satisfaction',
          'Gender Diversity', 'Safety Incidents', 'Board Independence', 'Ethics Training',
          'Renewable Energy %', 'Carbon Intensity', 'Supplier Assessment', 'Community Investment'
        ][i],
        metric_value: Math.random() * 100 + 10,
        unit: ['kWh', 'liters', 'kg', '%', '%', 'incidents', '%', 'hours', '%', 'tCO2e/revenue', 'score', 'USD'][i],
        reporting_period: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        scope: ['environmental', 'social', 'governance'][i % 3],
        data_source: ['Internal Systems', 'Third Party Audit', 'Self Assessment', 'External Report'][i % 4],
        validation_status: ['validated', 'pending', 'flagged'][i % 3]
      }));

      // Suppliers
      const suppliers = Array.from({ length: 8 }, (_, i) => ({
        user_id: user.id,
        supplier_name: [
          'Green Energy Corp', 'Sustainable Materials Ltd', 'Eco Transport Co',
          'Clean Tech Solutions', 'Renewable Resources Inc', 'Carbon Neutral Supplies',
          'Eco-Friendly Packaging', 'Sustainable Logistics'
        ][i],
        supplier_type: ['energy', 'materials', 'logistics', 'technology', 'raw_materials', 'services', 'packaging', 'transport'][i],
        industry_sector: ['Energy', 'Manufacturing', 'Transportation', 'Technology', 'Mining', 'Services', 'Packaging', 'Logistics'][i],
        annual_spend: Math.random() * 500000 + 50000,
        esg_assessment_score: Math.random() * 40 + 60,
        risk_level: ['low', 'medium', 'high'][i % 3],
        carbon_footprint: Math.random() * 1000 + 100,
        environmental_compliance: Math.random() > 0.3,
        social_compliance: Math.random() > 0.2,
        governance_compliance: Math.random() > 0.1,
        country: ['USA', 'Germany', 'Nigeria', 'China', 'India', 'Brazil', 'Japan', 'Canada'][i],
        currency: ['USD', 'EUR', 'NGN', 'CNY', 'INR', 'BRL', 'JPY', 'CAD'][i],
        water_usage: Math.random() * 5000 + 500,
        waste_generation: Math.random() * 200 + 20
      }));

      // Net zero targets
      const netZeroTargets = Array.from({ length: 4 }, (_, i) => ({
        user_id: user.id,
        target_name: ['Corporate Net Zero 2030', 'Supply Chain Emissions 2035', 'Product Lifecycle 2040', 'Operational Carbon Neutral 2028'][i],
        target_type: ['absolute', 'intensity', 'absolute', 'intensity'][i],
        baseline_year: 2020 + i,
        target_year: 2028 + (i * 3),
        baseline_emissions: Math.random() * 10000 + 5000,
        target_emissions: Math.random() * 3000 + 1000,
        current_emissions: Math.random() * 7000 + 3000,
        scope: ['scope1+2', 'scope3', 'scope1+2+3', 'scope1+2'][i],
        status: ['active', 'active', 'planning', 'active'][i],
        methodology: ['Science Based Targets', 'Internal Standard', 'ISO 14064', 'GHG Protocol'][i]
      }));

      // Portfolio investments
      const portfolioInvestments = Array.from({ length: 6 }, (_, i) => ({
        user_id: user.id,
        investment_name: [
          'Green Energy Fund', 'Sustainable Tech Portfolio', 'ESG Bond Fund', 
          'Climate Solutions ETF', 'Clean Water Investment', 'Renewable Infrastructure'
        ][i],
        investment_type: ['fund', 'equity', 'bond', 'etf', 'infrastructure', 'private_equity'][i],
        investment_amount: Math.random() * 1000000 + 100000,
        investment_date: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        esg_score: Math.random() * 30 + 70,
        environmental_score: Math.random() * 30 + 70,
        social_score: Math.random() * 30 + 70,
        governance_score: Math.random() * 30 + 70,
        sector: ['Energy', 'Technology', 'Finance', 'Mixed', 'Utilities', 'Infrastructure'][i],
        region: ['North America', 'Europe', 'Global', 'Asia Pacific', 'Latin America', 'Africa'][i],
        currency: ['USD', 'EUR', 'GBP', 'JPY', 'BRL', 'ZAR'][i]
      }));

      // Portfolio companies
      const portfolioCompanies = Array.from({ length: 5 }, (_, i) => ({
        user_id: user.id,
        company_name: [
          'GreenTech Innovations', 'Sustainable Solutions Inc', 'EcoFriendly Manufacturing',
          'Clean Energy Systems', 'Circular Economy Corp'
        ][i],
        industry_sector: ['Technology', 'Manufacturing', 'Energy', 'Services', 'Materials'][i],
        country: ['USA', 'Germany', 'Canada', 'Australia', 'Netherlands'][i],
        region: ['North America', 'Europe', 'North America', 'Oceania', 'Europe'][i],
        investment_date: new Date(Date.now() - Math.random() * 730 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        investment_amount: Math.random() * 5000000 + 500000,
        currency: ['USD', 'EUR', 'CAD', 'AUD', 'EUR'][i],
        ownership_percentage: Math.random() * 30 + 5,
        esg_score: Math.random() * 30 + 70,
        environmental_score: Math.random() * 30 + 70,
        social_score: Math.random() * 30 + 70,
        governance_score: Math.random() * 30 + 70,
        employee_count: Math.floor(Math.random() * 5000 + 100),
        revenue: Math.random() * 50000000 + 1000000,
        carbon_footprint: Math.random() * 10000 + 1000,
        water_usage: Math.random() * 50000 + 5000,
        waste_generation: Math.random() * 1000 + 100
      }));

      // Questionnaires
      const questionnaires = Array.from({ length: 3 }, (_, i) => ({
        user_id: user.id,
        questionnaire_name: ['ESG Assessment 2024', 'Supply Chain Sustainability', 'Carbon Management'][i],
        questionnaire_type: ['esg_assessment', 'supply_chain', 'carbon_management'][i],
        category: ['environmental', 'social', 'governance'][i],
        questions: JSON.stringify([
          {
            id: 1,
            question: "What percentage of your energy comes from renewable sources?",
            type: "number",
            required: true
          },
          {
            id: 2,
            question: "How do you measure and track your carbon emissions?",
            type: "text",
            required: true
          },
          {
            id: 3,
            question: "What sustainability certifications do you hold?",
            type: "multiple_choice",
            options: ["ISO 14001", "B-Corp", "LEED", "Energy Star", "None"],
            required: false
          }
        ]),
        responses: JSON.stringify({
          1: Math.floor(Math.random() * 100),
          2: "We use third-party verification and internal monitoring systems",
          3: ["ISO 14001", "LEED"]
        }),
        completion_status: ['completed', 'draft', 'in_progress'][i],
        completion_date: i === 0 ? new Date().toISOString().split('T')[0] : null,
        score: i === 0 ? Math.floor(Math.random() * 30 + 70) : null
      }));

      // Risk assessments
      const riskAssessments = Array.from({ length: 6 }, (_, i) => ({
        user_id: user.id,
        risk_name: [
          'Climate Change Impact', 'Supply Chain Disruption', 'Regulatory Compliance',
          'Water Scarcity', 'Energy Price Volatility', 'Reputation Risk'
        ][i],
        risk_category: ['environmental', 'operational', 'regulatory', 'environmental', 'financial', 'social'][i],
        risk_description: [
          'Physical and transition risks from climate change',
          'Potential disruptions to critical suppliers',
          'Changes in environmental regulations',
          'Limited access to water resources',
          'Fluctuations in energy costs',
          'Negative publicity affecting brand value'
        ][i],
        likelihood: Math.floor(Math.random() * 5 + 1),
        impact: Math.floor(Math.random() * 5 + 1),
        risk_score: Math.floor(Math.random() * 20 + 5),
        mitigation_strategy: 'Implement comprehensive monitoring and response protocols',
        responsible_person: ['John Smith', 'Sarah Johnson', 'Mike Chen', 'Lisa Brown', 'David Wilson', 'Emma Davis'][i],
        status: ['active', 'monitoring', 'mitigated'][i % 3],
        review_date: new Date(Date.now() + Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }));

      // Insert all dummy data
      await Promise.all([
        supabase.from('carbon_calculations').insert(carbonCalculations),
        supabase.from('user_metrics_data').insert(esgDataPoints.map(point => ({
          organization_id: user.id,
          name: point.metric_name,
          custom_value: point.metric_value,
          unit: point.unit,
          category: 'Environmental', // Default category
          subcategory: 'General',
          custom_metadata: {
            scope: point.scope,
            reporting_period: point.reporting_period,
            data_source: point.data_source,
            validation_status: point.validation_status
          }
        }))),
        supabase.from('suppliers').insert(suppliers),
        supabase.from('net_zero_targets').insert(netZeroTargets),
        supabase.from('portfolio_investments').insert(portfolioInvestments),
        supabase.from('portfolio_companies').insert(portfolioCompanies),
        supabase.from('questionnaires').insert(questionnaires),
        supabase.from('risk_assessments').insert(riskAssessments)
      ]);

      setHasData(true);
      toast({
        title: "Comprehensive Dummy Data Generated",
        description: "45+ sample entries created across all modules for testing and exploration.",
      });
    } catch (error) {
      console.error('Error generating dummy data:', error);
      toast({
        title: "Error",
        description: "Failed to generate dummy data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Delete all user data from all tables
      await Promise.all([
        supabase.from('carbon_calculations').delete().eq('user_id', user.id),
        supabase.from('user_metrics_data').delete().eq('organization_id', user.id),
        supabase.from('suppliers').delete().eq('user_id', user.id),
        supabase.from('net_zero_targets').delete().eq('user_id', user.id),
        supabase.from('portfolio_investments').delete().eq('user_id', user.id),
        supabase.from('portfolio_companies').delete().eq('user_id', user.id),
        supabase.from('questionnaires').delete().eq('user_id', user.id),
        supabase.from('risk_assessments').delete().eq('user_id', user.id),
        supabase.from('supply_chain_assessments').delete().eq('user_id', user.id),
        supabase.from('decarbonization_initiatives').delete().eq('user_id', user.id),
        supabase.from('sustainability_reports').delete().eq('user_id', user.id)
      ]);

      setHasData(false);
      toast({
        title: "All Data Cleared",
        description: "All organizational data has been deleted successfully. Analytics will now reset to zero.",
      });
    } catch (error) {
      console.error('Error clearing data:', error);
      toast({
        title: "Error",
        description: "Failed to clear data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Comprehensive Data Management
        </CardTitle>
        <CardDescription>
          Generate comprehensive sample data to explore all platform features or clear all data to start fresh with your real organizational data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This feature helps new users explore the complete platform with realistic sample data. 
            Use "Clear All Data" when ready to input your real organizational data. All analytics will automatically reset when data is cleared.
          </AlertDescription>
        </Alert>

        <div className="flex gap-4">
          <Button 
            onClick={generateDummyData}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {isLoading ? 'Generating...' : 'Generate Complete Sample Data'}
          </Button>

          <Button 
            onClick={clearAllData}
            disabled={isLoading}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isLoading ? 'Clearing...' : 'Clear All Data'}
          </Button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-2">Complete Sample Dataset Includes:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
            <div>
              <h5 className="font-medium text-gray-800">Environmental Data:</h5>
              <ul className="space-y-1 ml-2">
                <li>• 8 Carbon calculation entries (all scopes)</li>
                <li>• 12 ESG data points with validation</li>
                <li>• 4 Net-zero targets with progress tracking</li>
                <li>• 6 Risk assessments</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium text-gray-800">Business Data:</h5>
              <ul className="space-y-1 ml-2">
                <li>• 8 Supplier profiles with ESG scores</li>
                <li>• 6 Portfolio investments</li>
                <li>• 5 Portfolio companies</li>
                <li>• 3 ESG questionnaires with responses</li>
              </ul>
            </div>
          </div>
          <div className="mt-3 p-2 bg-blue-50 rounded">
            <p className="text-sm text-blue-800">
              <strong>Multi-Currency Support:</strong> Sample data includes transactions in USD, EUR, NGN, GBP, JPY, and other major currencies with real exchange rates.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
